/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  Output,
  Self,
  Optional,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XrxControlBaseComponent } from './xrx-control-base.component';

@Component({
  selector: 'xrx-file-input',
  templateUrl: './xrx-file-input.component.html',
  styleUrls: ['./xrx-file-input.component.less'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: XrxControlBaseComponent, useExisting: XrxFileInputComponent },
  ],
  standalone: false,
})
export class XrxFileInputComponent
  extends XrxControlBaseComponent
  implements ControlValueAccessor
{
  fileDragOver = false;
  fileFormatNotSupported = false;
  isValid = true;

  @Input() inModal: boolean;
  @Input() fileTypesAccepted: string;
  @Input() fileTypesString: string;
  @Input() id: string;
  @Input() name: string;
  @Input() disabled: boolean;
  @Input() required: boolean;
  @Input() multiple: boolean;

  @Output() fileDropped = new EventEmitter<{
    files: FileList;
    isValid: boolean;
  }>();

  isTruncated: boolean;

  input: any = {
    uploadFile: {},
    fileInputs: {},
  };

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private xdaStringsService: XdaStringsService,
  ) {
    super();
    this.ngControl.valueAccessor = this;
  }

  validate() {
    console.log('XrxMultiTranslationComponent.validate() called');
  }

  // update value of input.uploadFile when component is loaded
  writeValue(obj: any): void {
    // setTimeout because somewhere during initialization the default value is being erased
    setTimeout(() => {
      if (obj != null) {
        this.input.uploadFile = obj;
        if (this.input.uploadFile.name) {
          this.validateFileName(this.input.uploadFile.name);
        }
      }
    });
  }

  // function needed for ControlValueAccessor
  registerOnChange(fn: any): void {}

  // function needed for ControlValueAccessor
  registerOnTouched(fn: any): void {}

  // file drag start callback
  fileDragStart(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.fileDragOver = true;
  }
  // file end start callback
  fileDragEnd(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if ((event.target as Element).classList.contains('file-drag-overlay')) {
      this.fileDragOver = false;
    }
  }
  // file drop start callback
  fileDragDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.fileDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.inputChange(files);
    }
  }

  // input change (file selected, dropped or deleted) callback
  inputChange(files: FileList | null) {
    this.input.uploadFile = files?.item(0) || {};
    const fileName = this.input.uploadFile.name || '';
    this.isValid = true;

    // validate the fileName
    this.validateFileName(fileName);

    // emit the fileDropped event with a payload of the file and if it is valid
    if (files) {
      this.fileDropped.emit({ files: files, isValid: this.isValid });
    }
  }

  validateFileName(fileName: string) {
    this.fileFormatNotSupported = false;

    // file deleted
    if (!fileName) {
      this.ngControl.control?.markAsTouched();
      this.isValid = !this.required;
    }

    // if the fileName is empty and required is false set fileFormatNotSupported to false and isValid to true
    if (fileName == '' && this.required == false) {
      this.fileFormatNotSupported = false;
      this.isValid = true;
      // if fileName is empty and the field has been touched set fileFormatNotSupported to false
    } else if (fileName == '' && this.ngControl.control?.touched) {
      this.fileFormatNotSupported = false;
      // else there must be a file selected so validate it
    } else if (fileName) {
      // check to see if the file has a file extension
      const index = fileName.lastIndexOf('.');
      if (index === -1) {
        this.fileFormatNotSupported = true;
        this.isValid = false;
      }

      // check to see if the file is one that the input accepts
      const fileFormat = fileName.substr(index).toLowerCase();
      if (this.fileTypesAccepted.indexOf(fileFormat) === -1) {
        this.fileFormatNotSupported = true;
        this.isValid = false;
      }
    }

    // update ValueAndValidity if fileName is not empty
    if (fileName !== '') {
      this._updateValidation();
    }
  }

  checkTrucation(e: Event) {
    const trucated =
      (e.target as HTMLElement).offsetWidth !==
      (e.target as HTMLElement).scrollWidth;
    if (this.isTruncated === trucated) {
      return;
    }

    setTimeout(() => {
      if (
        (e.target as HTMLElement).offsetWidth !==
        (e.target as HTMLElement).scrollWidth
      ) {
        this.isTruncated = true;
      } else if (
        this.isTruncated !== false &&
        (e.target as HTMLElement).offsetWidth ===
          (e.target as HTMLElement).scrollWidth
      ) {
        this.isTruncated = false;
      }
    }, 1);
  }

  // reset field function
  resetInputSettings() {
    this.input.uploadFile = {};
    this.fileFormatNotSupported = false;

    setTimeout(() => {
      const control = this.ngControl.control;
      control?.markAsUntouched();
      control?.markAsPristine();
      control?.updateValueAndValidity();
    }, 1);
  }

  // validate input function
  validateInput() {
    // update ValueAndValidity when parent component requests field validation
    this._updateValidation();
  }

  // update control's validation
  _updateValidation() {
    setTimeout(() => {
      const control = this.ngControl.control;
      control?.markAsDirty();
      control?.markAsTouched();
      control?.updateValueAndValidity();
    }, 1);
  }
}

export interface FileInput {
  files: FileList;
  isValid: boolean;
}
